import { useApolloClient } from '@apollo/client';
import {
  Button,
  CheckboxFormField,
  DatePickerFormField,
  Form,
  IFormRender,
  Label,
  Loader,
  TextFormField,
  Theme,
  Toast,
} from '@busybusy/webapp-react-ui';
import FlexContainer from '@busybusy/webapp-react-ui/dist/components/FlexContainer/FlexContainer';
import { Equipment, EquipmentCostHistory, EquipmentModelCreateInput } from '__generated__/graphql';
import { EQUIPMENT_BY_ID_QUERY } from 'apollo/queries/equipment-queries';
import classNames from 'classnames';
import { ProfileImageFormField } from 'components';
import EquipmentCategoryFormField from 'components/domain/equipment/EquipmentCategoryFormField/EquipmentCategoryFormField';
import EquipmentMakeFormField from 'components/domain/equipment/EquipmentMakeFormField/EquipmentMakeFormField';
import EquipmentModelFormField from 'components/domain/equipment/EquipmentModelFormField/EquipmentModelFormField';
import { equipmentUnknownModelQuery } from 'components/domain/equipment/EquipmentModelsForMakePicker/equipment-models-picker-queries';
import EquipmentTypeFormField from 'components/domain/equipment/EquipmentTypeFormField/EquipmentTypeFormField';
import CurrencyFormField from 'components/foundation/form-fields/CurrencyFormField/CurrencyFormField';
import useEquipmentImageUpdate from 'containers/manage-employees/hooks/useEquipmentImageUpdate';
import useEquipmentModels from 'containers/manage-euipment/hooks/useEquipmentModels';
import { useEquipment, useEquipmentHours, useOpenable } from 'hooks';
import useCreateEquipmentCostHistory from 'hooks/models/equipment-cost-history/useCreateEquipmentCostHistory';
import useUpdateEquipmentCostHistory from 'hooks/models/equipment-cost-history/useUpdateEquipmentCostHistory';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import _, { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode, useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import EquipmentModel from 'types/EquipmentModel';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { getCurrentEquipmentCostHistory } from 'utils/equipmentUtils';
import { t } from 'utils/localize';
import { convertStringToSafeNumber } from 'utils/numberUtils';
import { v_number_decimals_only, v_require } from 'utils/validations';
import { IEquipmentCreateFormData } from '../EquipmentCreateForm/EquipmentCreateForm';
import { IExistingModel } from '../EquipmentModelPicker/EquipmentModelPicker';
import EquipmentTypesDialog from '../EquipmentTypesDialog/EquipmentTypesDialog';
import ManageEquipmentCategoriesDialog from '../ManageEquipmentCategoriesDialog/ManageEquipmentCategoriesDialog';
import ManageEquipmentMakesDialog from '../ManageEquipmentMakesDialog/ManageEquipmentMakesDialog';
import './EditEquipmentForm.scss';

export interface IEditEquipmentFormProps {
  className?: ClassName;
  equipmentId: string;
  editKey: string | null;
  onSubmit: (data?: string) => void;
}

const EditEquipmentForm = (props: IEditEquipmentFormProps) => {
  const { className, equipmentId, editKey, onSubmit } = props;

  const [formData, setFormData] = useState<IEquipmentCreateFormData>();
  const [reloadCategories, setReloadCategories] = useState(false);
  const [modelToCreate, setModelToCreate] = useState<{ id: string | null; name: string | null }>();

  const { editEquipment } = useEquipment();
  const { createEquipmentHours } = useEquipmentHours();
  const { createEquipmentModel } = useEquipmentModels();
  const errorToastState = useOpenable();
  const errorToastMessage = useRef(t('There was an unexpected error.'));
  const apolloClient = useApolloClient();
  const imageUpdate = useEquipmentImageUpdate();
  const loaderDetails = useOpenable();
  const dateDetails = useOpenable();
  const manageCategoriesOpenable = useOpenable();
  const manageMakesOpenable = useOpenable();
  const typesDialogOpenable = useOpenable();
  const createEquipmentCostHistory = useCreateEquipmentCostHistory();
  const updateEquipmentCostHistory = useUpdateEquipmentCostHistory();
  const today = DateTime.local().startOf('day');
  const latestCostHistory = useRef<EquipmentCostHistory | undefined>(undefined);

  const classes = classNames(
    {
      'edit-equipment-form': true,
    },
    className
  );

  useOnMount(() => {
    getEquipment().then((equipment) => {
      latestCostHistory.current = getCurrentEquipmentCostHistory(equipment!.costHistory ?? []);

      const hourlyCostEffectiveDate = !isNil(latestCostHistory.current?.changeDate)
        ? DateTime.fromISO(latestCostHistory.current!.changeDate)
        : today;

      setFormData({
        equipmentName: equipment!.equipmentName,
        type: equipment!.model?.type,
        categoryId: equipment!.model?.equipmentCategoryId,
        makeId: equipmentMakeId(equipment!),
        modelId: equipmentModelId(equipment!),
        modelTitle: equipmentModelTitle(equipment!),
        trackManualHours: equipment?.trackManualHours,
        hourMeter: equipment!.lastHours?.runningHours ?? 0,
        imageUrl: equipment?.imageUrl ?? undefined,
        hourlyCost: latestCostHistory.current?.operatorCostRate ?? 0,
        hourlyCostEffectiveDate: {
          value: hourlyCostEffectiveDate.toJSDate(),
          inputValue: hourlyCostEffectiveDate.toFormat('M/D/YYYY'),
        },
        year: equipment?.year ?? undefined,
      });
    });
  });

  const handleCategoriesDialogClose = () => {
    manageCategoriesOpenable.close();
    setReloadCategories((prev) => !prev);
  };

  const handleMakeOrCategoryChange = (form: IFormRender<IEquipmentCreateFormData>) => {
    form.setFormData({ ...form.state.data, modelId: undefined });
  };

  const handleExistingModelSelected = (model: IExistingModel, form: IFormRender<IEquipmentCreateFormData>) => {
    form.setFormData({
      ...form.state.data,
      makeId: model.makeId,
      categoryId: model.categoryId,
    });
  };

  const equipmentMakeId = (equipment: Equipment) => {
    if (equipment?.model?.make?.unknown === false) {
      return equipment.model.equipmentMakeId;
    }

    return undefined;
  };

  const equipmentModelId = (equipment: Equipment) => {
    if (equipment?.model?.unknown === false) {
      return equipment.model.id;
    }
    return undefined;
  };

  const equipmentModelTitle = (equipment: Equipment) => {
    if (equipment?.model?.unknown === false) {
      return equipment.model.title as string;
    }

    return undefined;
  };

  const getEquipment = async () => {
    const results = await apolloClient.query<{ equipment: Equipment[] }>({
      query: EQUIPMENT_BY_ID_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        equipmentId,
      },
    });

    return _.first(results.data.equipment);
  };

  const equipmentNameField = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label required={true}>{t('Name')}</Label>
      <TextFormField name={'equipmentName'} form={form} validations={[{ validation: v_require }]} autofocus={true} />
    </>
  );

  const equipmentTypePicker = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Type')}</Label>
      <span className="types-help-text">
        {t('To learn more about types, ')}
        <span className="types-link" onClick={typesDialogOpenable.open}>
          {t('click here.')}
        </span>
      </span>
      <EquipmentTypeFormField
        name={'type'}
        form={form}
        validations={[{ validation: v_require }]}
        autofocus={autofocus}
        onChange={() => handleMakeOrCategoryChange(form)}
      />
    </>
  );

  const equipmentCategoryPicker = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <FlexContainer justifyContent={'space-between'}>
        <Label className={'py-2'}>{t('Category')}</Label>
        <Button type="link" onClick={manageCategoriesOpenable.open}>
          {t('Manage')}
        </Button>
      </FlexContainer>
      <EquipmentCategoryFormField
        name={'categoryId'}
        form={form}
        validations={[{ validation: v_require }]}
        reload={reloadCategories}
        onChange={() => handleMakeOrCategoryChange(form)}
        disabled={isNil(form.state.data.type)}
      />
    </>
  );

  const equipmentMakePicker = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <FlexContainer justifyContent={'space-between'}>
        <Label className={'py-2'}>{t('Make')}</Label>
        <Button type="link" onClick={manageMakesOpenable.open}>
          {t('Manage')}
        </Button>
      </FlexContainer>
      <EquipmentMakeFormField
        name={'makeId'}
        equipmentCategoryId={form.state.data.categoryId}
        form={form}
        autofocus={autofocus}
        onChange={() => handleMakeOrCategoryChange(form)}
        disabled={isNil(form.state.data.type)}
        validations={[{ validation: v_require }]}
      />
    </>
  );

  const equipmentModelPicker = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label>{t('Model')}</Label>
      <EquipmentModelFormField
        name={'modelId'}
        equipmentMakeId={form.state.data.makeId}
        equipmentCategoryId={form.state.data.categoryId}
        equipmentModelType={form.state.data.type}
        form={form}
        onExistingModel={(model) => {
          handleExistingModelSelected(model, form);
        }}
        onNewModel={setModelToCreate}
        disabled={isNil(form.state.data.type)}
        validations={[{ validation: v_require }]}
      />
    </>
  );

  const equipmentYearFormField = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Year')}</Label>
      <TextFormField className="year-field" name="year" form={form} autofocus={autofocus} />
    </>
  );

  const equipmentHoursField = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Hour Meter')}</Label>
      <TextFormField
        className="hour-meter-field"
        type="number"
        name={'hourMeter'}
        placeholder={'0.0'}
        form={form}
        restrictTo={{ maxLength: 10 }}
        validations={[{ validation: v_number_decimals_only }]}
        autofocus={autofocus}
      />
    </>
  );

  const trackHoursCheckbox = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <CheckboxFormField name="trackManualHours" label={t('Manually Track Hours')} form={form} />
    </>
  );

  const equipmentHourlyCostField = (form: IFormRender<IEquipmentCreateFormData>, autofocus: boolean = false) => (
    <>
      <Label>{t('Hourly Cost')}</Label>
      <CurrencyFormField
        className="hourly-cost-field"
        name="hourlyCost"
        form={form}
        validations={[{ validation: v_require }]}
        textAlign="right"
        placeholder="0.00"
        onBlur={(value) => {
          form.setData('hourlyCost', convertStringToSafeNumber(value).toFixed(2));
        }}
        autofocus={autofocus}
      />
      <Label>{t('Effective Date')}</Label>
      <div className="mb-2">
        {t(
          'The date you would like the rate change to take effect. Setting the date to the past will change past job costing data.'
        )}
      </div>
      <DatePickerFormField
        name="hourlyCostEffectiveDate"
        form={form}
        disabledBefore={
          !isNil(latestCostHistory.current?.changeDate)
            ? DateTime.fromISO(latestCostHistory.current!.changeDate).toJSDate()
            : undefined
        }
        validations={[{ validation: v_require }]}
        isOpen={dateDetails.isOpen}
        onClose={dateDetails.close}
        onOpen={dateDetails.open}
      />
    </>
  );

  const editButton = (form: IFormRender<IEquipmentCreateFormData>) => (
    <Button className="pl-5 pr-5" type="primary" onClick={form.handleSubmit}>
      {t('Save')}
    </Button>
  );

  const imageField = (form: IFormRender<IEquipmentCreateFormData>) => (
    <>
      <Label>{t('Equipment Image')}</Label>
      <ProfileImageFormField name="image" initialImage={form.state.data.imageUrl ?? null} form={form} />
    </>
  );

  function renderFormFields({ ...form }: IFormRender<IEquipmentCreateFormData>): ReactNode {
    switch (editKey) {
      case 'name':
        return (
          <div>
            {equipmentNameField(form)}
            {editButton(form)}
          </div>
        );
      case 'type':
        return (
          <div>
            {equipmentTypePicker(form, true)}
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'category':
        return (
          <div>
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'make':
        return (
          <div>
            {equipmentTypePicker(form, true)}
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'model':
        return (
          <div>
            {equipmentModelPicker(form)}
            {editButton(form)}
          </div>
        );
      case 'year':
        return (
          <div>
            {equipmentYearFormField(form, true)}
            {editButton(form)}
          </div>
        );
      case 'hourMeter':
        return (
          <div>
            {trackHoursCheckbox(form)}
            {form.state.data.trackManualHours ? equipmentHoursField(form, true) : null}
            {editButton(form)}
          </div>
        );
      case 'hourlyCost':
        return (
          <div>
            {equipmentHourlyCostField(form, true)}
            {editButton(form)}
          </div>
        );
      case 'image':
        return (
          <div>
            {imageField(form)}
            {editButton(form)}
          </div>
        );
      case null:
        return (
          <div>
            {equipmentNameField(form)}
            {equipmentTypePicker(form)}
            {equipmentCategoryPicker(form)}
            {equipmentMakePicker(form)}
            {equipmentModelPicker(form)}
            {equipmentYearFormField(form)}
            {trackHoursCheckbox(form)}
            {form.state.data.trackManualHours ? equipmentHoursField(form) : null}
            {editButton(form)}
          </div>
        );
    }
  }

  const getUnknownModel = async (categoryId: string) => {
    const results = await apolloClient.query<{ equipmentModels: EquipmentModel[] }>({
      query: equipmentUnknownModelQuery,
      variables: {
        categoryId,
      },
    });

    return _.first(results.data.equipmentModels);
  };

  async function onSubmitForm(data: IEquipmentCreateFormData) {
    loaderDetails.open();
    if (validateData(data)) {
      let modelId = data.modelId;

      if (!modelId) {
        const unknownModel = await getUnknownModel(data.categoryId!);
        modelId = unknownModel?.id;
      }

      if (!modelId) {
        // could not find unknown model
        errorToastMessage.current = t('Cannot save without a make and model selected.');
        errorToastState.open();
        return;
      }

      if (!isNil(modelToCreate)) {
        const input: EquipmentModelCreateInput = {
          createdOn: isoTimeStampUtc(),
          equipmentCategoryId: data.categoryId!,
          equipmentMakeId: data.makeId!,
          title: modelToCreate.name!,
          type: data.type!,
          id: modelToCreate.id,
        };
        createEquipmentModel(input);
      }

      editEquipment(equipmentId, data.equipmentName!, modelId, data.trackManualHours === true, data.year)
        .then(async (response) => {
          if (data.image !== formData?.image) {
            await imageUpdate(equipmentId, data.image?.file ?? null).catch(() => {
              errorToastMessage.current = t('There was an error saving the image.');
              errorToastState.open();
              loaderDetails.close();
            });
          }

          const equipmentHours = response.data.updateEquipment.id;
          let shouldSubmit = true;

          if (data.hourMeter && data.hourMeter && data.hourMeter !== formData?.hourMeter) {
            shouldSubmit = false;
            // save the hour meter if the user input a valid value
            createEquipmentHours(equipmentHours, data.hourMeter)
              .then(() => {
                onSubmit(response.data.updateEquipment.id);
              })
              .catch(() => {
                errorToastMessage.current = t('There was an error saving the hour meter.');
                errorToastState.open();
              });
          }

          if (data.hourlyCost && data.hourlyCostEffectiveDate) {
            if (
              (isNil(latestCostHistory.current?.changeDate) && data.hourlyCost !== formData?.hourlyCost) ||
              (data.hourlyCostEffectiveDate?.value &&
                !isNil(latestCostHistory.current?.changeDate) &&
                DateTime.fromJSDate(data.hourlyCostEffectiveDate.value).startOf('day').toISO() !==
                  DateTime.fromISO(latestCostHistory.current!.changeDate).startOf('day').toISO())
            ) {
              shouldSubmit = false;
              // save the hourly cost if the user input a valid value
              createEquipmentCostHistory(
                equipmentHours,
                undefined,
                data.hourlyCost,
                DateTime.fromJSDate(data.hourlyCostEffectiveDate.value!).startOf('day').toISO()
              )
                .then(() => {
                  onSubmit(response.data.updateEquipment.id);
                })
                .catch(() => {
                  errorToastMessage.current = t('There was an error saving cost history.');
                  errorToastState.open();
                });
            } else if (!isNil(latestCostHistory.current) && data.hourlyCost !== formData?.hourlyCost) {
              shouldSubmit = false;
              // save the hourly cost if the user input a valid value
              updateEquipmentCostHistory(latestCostHistory.current.id, undefined, data.hourlyCost)
                .then(() => {
                  onSubmit(response.data.updateEquipment.id);
                })
                .catch(() => {
                  errorToastMessage.current = t('There was an error saving cost history.');
                  errorToastState.open();
                });
            }
          }

          if (shouldSubmit) {
            onSubmit(response.data.updateEquipment.id);
          }

          loaderDetails.close();
        })
        .catch(() => {
          errorToastMessage.current = t('There was an error saving the equipment.');
          errorToastState.open();
          loaderDetails.close();
        });
    } else {
      errorToastMessage.current = t('Answer all of the questions and try again.');
      errorToastState.open();
      loaderDetails.close();
    }
  }

  function validateData(data: IEquipmentCreateFormData): boolean {
    if (_.isEmpty(data.equipmentName)) {
      return false;
    } else if (!_.isNil(data.makeId) && _.isNil(data.modelId)) {
      // if make is selected then we also need a model
      return false;
    }

    return true;
  }

  return (
    <div className={classes}>
      <div className="mb-6 p-8">
        {formData && <Form data={formData} onSubmit={onSubmitForm} render={renderFormFields} className={classes} />}

        <Toast isOpen={errorToastState.isOpen} onClose={errorToastState.close} theme={Theme.DANGER}>
          {errorToastMessage.current}
        </Toast>
        <Loader isOpen={loaderDetails.isOpen} />
        <ManageEquipmentCategoriesDialog
          isOpen={manageCategoriesOpenable.isOpen}
          onClose={handleCategoriesDialogClose}
        />

        <ManageEquipmentMakesDialog isOpen={manageMakesOpenable.isOpen} onClose={manageMakesOpenable.close} />
        <EquipmentTypesDialog isOpen={typesDialogOpenable.isOpen} onClose={typesDialogOpenable.close} />
      </div>
    </div>
  );
};

export default EditEquipmentForm;
